import * as Sentry from '@sentry/react'

const sentryDsn = (import.meta.env.VITE_SENTRY_DSN as string | undefined)?.trim()
const appEnvironment =
  ((import.meta.env.VITE_APP_ENV as string | undefined) ?? import.meta.env.MODE)?.trim()
const appRelease = (import.meta.env.VITE_RELEASE_SHA as string | undefined)?.trim()

let initialized = false

type MonitoringUser = {
  id: string
  role?: string
  email?: string
}

type MonitoringContext = {
  tags?: Record<string, string>
  extra?: Record<string, unknown>
}

type MonitoringSmokeResult = {
  sent: boolean
  eventId: string | null
}

declare global {
  interface Window {
    __EIGYO_MONITORING__?: {
      source: 'eigyo-cms-demo'
      initialized: boolean
      hasDsn: boolean
      environment: string | null
      release: string | null
      captureSmoke: (smokeId: string) => Promise<MonitoringSmokeResult>
    }
  }
}

function toError(error: unknown): Error {
  if (error instanceof Error) return error
  if (typeof error === 'string') return new Error(error)
  try {
    return new Error(JSON.stringify(error))
  } catch {
    return new Error(String(error))
  }
}

function syncMonitoringBridge() {
  if (typeof window === 'undefined') return

  window.__EIGYO_MONITORING__ = {
    source: 'eigyo-cms-demo',
    initialized: Boolean(Sentry.getClient()),
    hasDsn: Boolean(sentryDsn),
    environment: appEnvironment ?? null,
    release: appRelease ?? null,
    captureSmoke: async (smokeId: string) => {
      const client = Sentry.getClient()
      if (!client || !sentryDsn) return { sent: false, eventId: null }

      const eventId = Sentry.captureMessage(`codex sentry smoke ${smokeId}`, 'info')
      await client.flush(5000)
      return { sent: true, eventId }
    },
  }
}

export function initMonitoring() {
  if (initialized || !sentryDsn) {
    syncMonitoringBridge()
    return
  }

  Sentry.init({
    dsn: sentryDsn,
    environment: appEnvironment,
    release: appRelease,
  })

  initialized = true
  syncMonitoringBridge()
}

export function captureAppException(
  error: unknown,
  context: MonitoringContext = {},
) {
  if (!sentryDsn) return

  Sentry.withScope((scope) => {
    for (const [key, value] of Object.entries(context.tags ?? {})) {
      scope.setTag(key, value)
    }

    for (const [key, value] of Object.entries(context.extra ?? {})) {
      scope.setExtra(key, value)
    }

    Sentry.captureException(toError(error))
  })
}

export function setMonitoringUser(user: MonitoringUser | null) {
  if (!sentryDsn) return

  if (!user) {
    Sentry.setUser(null)
    return
  }

  Sentry.setUser({
    id: user.id,
    role: user.role,
    email: user.email,
  })
}
